import { isPortfolioClaimSignUpContent } from '@extern/portfolio/utils/portfolio-sign-up-url';

/**
 * Clerk `unsafeMetadata`: this account signed up from a portfolio page it had already claimed
 * anonymously. Device-independent, following `portfolio_walkthrough_completed`.
 *
 * 🔴 CONTENT CANNOT EXPRESS THIS. `hasPortfolioContent` asks whether the page carries work its owner
 * put there, and a visitor may claim a link, skip the resume and type nothing — that page is
 * identical on the wire to one an in-product turn-on just created empty. Product ruled that coming
 * from a claimed page is itself the qualifier, so the origin has to be recorded, not inferred.
 *
 * Written at sign-up because that is the last moment the origin is knowable: adoption happens in the
 * backend by matching the email, and the editor is only opened after verification, the application
 * questionnaire and checkout — possibly on another device, which is why this is on the account
 * rather than in the browser.
 */
export const PORTFOLIO_CLAIMED_AT_SIGN_UP_KEY = 'portfolio_claimed_at_sign_up';

const PORTFOLIO_CLAIMED_AT_SIGN_UP_METADATA = { [PORTFOLIO_CLAIMED_AT_SIGN_UP_KEY]: true } as const;

/**
 * The marker to write, or undefined for every other arrival — so it spreads into a payload without
 * a branch, and ordinary sign-up traffic produces byte-for-byte the metadata it produces today.
 * That inertness is the point: sign-up carries a live A/B test (`ab_test_sign_up_2026_q1`) and a new
 * write must not become a silent third condition in it.
 *
 * Derived from `utm_content`, so anyone can hand-type a URL that sets it. All it buys is skipping
 * onboarding, which is not a privilege, so it is not worth a signature.
 */
export function portfolioClaimSignUpMetadata(
  utmContent: string | null | undefined,
): typeof PORTFOLIO_CLAIMED_AT_SIGN_UP_METADATA | undefined {
  return isPortfolioClaimSignUpContent(utmContent) ? PORTFOLIO_CLAIMED_AT_SIGN_UP_METADATA : undefined;
}

export function hasClaimedPortfolioAtSignUp(unsafeMetadata: Record<string, unknown> | null | undefined): boolean {
  return unsafeMetadata?.[PORTFOLIO_CLAIMED_AT_SIGN_UP_KEY] === true;
}
